import { DrawItem } from '@unpispas/upp-data';
import { FloorArea } from '@unpispas/upp-data';
import { QrCode } from '@unpispas/upp-data';
import { UPP_TABLE_CHAIRS_MAX } from '@unpispas/upp-data';
import { UPP_TABLE_CHAIRS_MIN } from '@unpispas/upp-data';

/**
 * Floor-area orchestration shared by edit, view, shapes and Konva projection
 * ({@link FloorArea.draws}, table QR links, draw identity).
 */
export class UppFloorUtils {
    /**
     * Resolves chair count for TABLE draws: valid {@link DrawItem.qrcode} value first, then JSON in
     * {@link DrawItem.custom} (same priority as `upp-shape-table`).
     */
    static tableChairsFromDraw(draw: DrawItem): number | null {
        const minC = UPP_TABLE_CHAIRS_MIN;
        const maxC = UPP_TABLE_CHAIRS_MAX;

        const qrRaw = Number(draw.qrcode?.chairs);
        if (Number.isFinite(qrRaw)) {
            const rounded = Math.round(qrRaw);
            if (rounded >= minC && rounded <= maxC) {
                return rounded;
            }
        }

        const raw = draw.custom;
        if (typeof raw !== 'string' || raw.trim() === '') {
            return null;
        }

        try {
            const o = JSON.parse(raw) as unknown;
            if (o && typeof o === 'object' && !Array.isArray(o) && 'chairs' in o) {
                const c = (o as { chairs?: unknown }).chairs;
                const n = typeof c === 'number' ? c : parseInt(String(c), 10);
                if (Number.isFinite(n)) {
                    const rounded = Math.round(n);
                    if (rounded >= minC && rounded <= maxC) {
                        return rounded;
                    }
                }
            }
        } catch {
            // ignore malformed JSON
        }

        return null;
    }

    /** Shape `data` bag for TABLE draws (also used when syncing panel → Konva in edit). */
    static TableFromDraw(draw: DrawItem): Record<string, unknown> {
        const data: Record<string, unknown> = {};

        const chairs = UppFloorUtils.tableChairsFromDraw(draw);
        if (chairs != null) {
            data['chairs'] = chairs;
            draw.custom = JSON.stringify({ chairs });
        }

        const variant = typeof draw.shape === 'string' ? draw.shape.trim() : '';
        if (variant !== '') {
            data['shape'] = variant.toUpperCase() === 'CIRCLE' ? 'CIRCLE' : 'SQUARE';
        }

        return data;
    }

    /** Shape `data` bag for non-vertex, non-table draws (also used when syncing panel → Konva in edit). */
    static OtherFromDraw(draw: DrawItem): Record<string, unknown> {
        let extra: Record<string, unknown> = {};
        const rawCustom = draw.custom;
        if (typeof rawCustom === 'string' && rawCustom.trim() !== '') {
            try {
                const parsed = JSON.parse(rawCustom) as unknown;
                if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                    extra = { ...(parsed as Record<string, unknown>) };
                }
            } catch {
                // ignore malformed JSON
            }
        }

        const variant = typeof draw.shape === 'string' ? draw.shape.trim() : '';
        const data: Record<string, unknown> = { ...extra };
        if (variant !== '') {
            data['shape'] = variant;
        }

        return data;
    }

    /**
     * Resolves a live canvas shape id to the canonical {@link DrawItem} on the floor area.
     */
    static ResolveDrawInArea(area: FloorArea, shapeId: string): DrawItem | null {
        const targetId = shapeId.trim();
        if (targetId === '') {
            return null;
        }

        for (const draw of area.draws) {
            if (!draw.IsValid) {
                continue;
            }

            if (draw._uuid.trim() === targetId) {
                return draw;
            }

            const linked = draw.konva as { id?: string } | null;
            if (linked != null && String(linked.id ?? '').trim() === targetId) {
                return draw;
            }
        }

        return null;
    }

    /** Whether {@link linked} and {@link draw} reference the same DRAWITEM row (`_uuid` / `objid`). */
    private static SameDrawRow(draw: DrawItem, linked: DrawItem): boolean {
        const drawUuid = draw._uuid.trim();
        const linkedUuid = linked._uuid.trim();
        if (drawUuid !== '' && drawUuid === linkedUuid) {
            return true;
        }

        const drawObjid = draw.objid;
        const linkedObjid = linked.objid;
        return !!drawObjid && !!linkedObjid && drawObjid === linkedObjid;
    }

    /**
     * Points each table {@link QrCode.draw} at the canonical {@link DrawItem} row in {@link FloorArea.draws}.
     */
    static RefreshTableQrcodeDrawRefs(area: FloorArea): void {
        const place = area.place;
        if (!place) {
            return;
        }

        for (const draw of area.draws) {
            if (!draw.IsValid || String(draw.type ?? '').trim().toUpperCase() !== 'TABLE') {
                continue;
            }

            for (const qrcode of place.qrcodes ?? []) {
                if (!qrcode.IsValid) {
                    continue;
                }

                const linked = qrcode.draw as DrawItem | null;
                if (linked && linked !== draw && UppFloorUtils.SameDrawRow(draw, linked)) {
                    qrcode.draw = draw;
                }
            }
        }
    }

    /**
     * Forces every table {@link QrCode.draw} to re-bind to its linked {@link DrawItem} row so the stored
     * foreign key captures the DRAWITEM `objid`. Required after persisting the floor area.
     *
     * @returns Table QR codes whose {@link QrCode.draw} was re-bound in this area.
     */
    static RelinkTableQrcodeDraws(area: FloorArea): QrCode[] {
        const place = area.place;
        if (!place) {
            return [];
        }

        const relinked: QrCode[] = [];
        const _draws = new Set(area.draws);
        for (const qrcode of place.qrcodes ?? []) {
            if (!qrcode.IsValid) {
                continue;
            }

            const linked = qrcode.draw as DrawItem | null;
            if (!linked || !linked.IsValid || !_draws.has(linked)) {
                continue;
            }

            if (String(linked.type ?? '').trim().toUpperCase() !== 'TABLE') {
                continue;
            }

            qrcode.draw = null;
            qrcode.draw = linked;

            relinked.push(qrcode);
        }

        return relinked;
    }
}
